/**
 * MietvertragPage — Digitaler Mietvertrag für Unfallersatzfahrzeuge
 * 
 * Features:
 * - Mietvertrag erstellen mit allen relevanten Daten
 * - Fahrzeugauswahl aus Supabase (Rentsoft-synchronisiert)
 * - Digitale Unterschrift (Canvas-basiert, signature_pad)
 * - PDF-Generierung und Speicherung in Supabase Storage
 * - Verknüpfung mit Forderungsakten
 * 
 * v1.0 — 07.04.2026
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import SignaturePad from 'signature_pad';
import {
  FileText, Car, User, Calendar, Euro, Pen, Trash2,
  CheckCircle2, Download, AlertCircle, Shield, ChevronLeft,
  ChevronRight, Plus, Search, Printer, Eye
} from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const KOOP_ROT = '#C41E3A';

// ── Types ──
interface MietvertragFormData {
  // Mieter
  mieter_anrede: string;
  mieter_vorname: string;
  mieter_nachname: string;
  mieter_strasse: string;
  mieter_plz: string;
  mieter_ort: string;
  mieter_telefon: string;
  mieter_email: string;
  mieter_fuehrerschein_nr: string;
  mieter_fuehrerschein_klasse: string;
  mieter_geburtsdatum: string;
  // Unfall
  unfall_datum: string;
  unfall_ort: string;
  unfallgegner_name: string;
  unfallgegner_kennzeichen: string;
  versicherung_gegner: string;
  schaden_nummer: string;
  // Fahrzeug
  fahrzeug_id: string;
  fahrzeug_kennzeichen: string;
  fahrzeug_marke: string;
  fahrzeug_modell: string;
  fahrzeug_kategorie: string;
  fahrzeug_km_stand: number;
  // Mietkonditionen
  mietbeginn: string;
  mietende: string;
  tagessatz_netto: number;
  eigenersparnis_prozent: number;
  kaution: number;
  km_frei_pro_tag: number;
  km_mehrpreis: number;
  // Beschädigtes Fahrzeug
  beschaedigt_kennzeichen: string;
  beschaedigt_marke: string;
  beschaedigt_modell: string;
  beschaedigt_klasse: string;
  // Sonstiges
  notizen: string;
}

interface Fahrzeug {
  id: string;
  marke: string;
  modell: string;
  kennzeichen: string;
  kategorie: string;
  km_aktuell: number;
  status: string;
}

const EMPTY_FORM: MietvertragFormData = {
  mieter_anrede: 'Herr',
  mieter_vorname: '',
  mieter_nachname: '',
  mieter_strasse: '',
  mieter_plz: '',
  mieter_ort: '',
  mieter_telefon: '',
  mieter_email: '',
  mieter_fuehrerschein_nr: '',
  mieter_fuehrerschein_klasse: 'B',
  mieter_geburtsdatum: '',
  unfall_datum: new Date().toISOString().split('T')[0],
  unfall_ort: '',
  unfallgegner_name: '',
  unfallgegner_kennzeichen: '',
  versicherung_gegner: '',
  schaden_nummer: '',
  fahrzeug_id: '',
  fahrzeug_kennzeichen: '',
  fahrzeug_marke: '',
  fahrzeug_modell: '',
  fahrzeug_kategorie: '',
  fahrzeug_km_stand: 0,
  mietbeginn: new Date().toISOString().split('T')[0],
  mietende: '',
  tagessatz_netto: 65,
  eigenersparnis_prozent: 3.5,
  kaution: 150,
  km_frei_pro_tag: 250,
  km_mehrpreis: 0.25,
  beschaedigt_kennzeichen: '',
  beschaedigt_marke: '',
  beschaedigt_modell: '',
  beschaedigt_klasse: '',
  notizen: '',
};

function formatDatum(d?: Date): string {
  const date = d || new Date();
  return date.toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' });
}

function formatEuro(val: number): string {
  return val.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' EUR';
}

// ── Schritt-Konfiguration ──
const STEPS = [
  { id: 1, label: 'Mieter', icon: User, desc: 'Persönliche Daten' },
  { id: 2, label: 'Unfall', icon: AlertCircle, desc: 'Unfalldaten & Versicherung' },
  { id: 3, label: 'Fahrzeug', icon: Car, desc: 'Mietfahrzeug wählen' },
  { id: 4, label: 'Konditionen', icon: Euro, desc: 'Mietkonditionen' },
  { id: 5, label: 'Vertrag', icon: FileText, desc: 'Prüfen & Unterschreiben' },
];

export default function MietvertragPage() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState<MietvertragFormData>({ ...EMPTY_FORM });
  const [fahrzeuge, setFahrzeuge] = useState<Fahrzeug[]>([]);
  const [loading, setLoading] = useState(true);
  const [vertraege, setVertraege] = useState<any[]>([]);
  const [showList, setShowList] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Signature
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const signaturePadRef = useRef<SignaturePad | null>(null);
  const [hasSignature, setHasSignature] = useState(false);
  const [agbAccepted, setAgbAccepted] = useState(false);
  const [datenschutzAccepted, setDatenschutzAccepted] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [unterschriftDatum, setUnterschriftDatum] = useState(formatDatum());

  const set = (key: keyof MietvertragFormData, val: any) => setForm(prev => ({ ...prev, [key]: val }));

  // ── Fahrzeuge laden ──
  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from('fahrzeuge')
        .select('id, marke, modell, kennzeichen, kategorie, km_aktuell, status')
        .in('status', ['aktiv', 'verfuegbar'])
        .order('kennzeichen');
      setFahrzeuge(data || []);
      setLoading(false);
    })();
  }, []);

  // ── Bestehende Verträge laden ──
  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from('forderungsakten')
        .select('*')
        .not('mietvertrag_nr', 'is', null)
        .order('created_at', { ascending: false });
      setVertraege(data || []);
    })();
  }, []);

  // ── Signature Pad init ──
  useEffect(() => {
    if (step !== 5 || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext('2d');
    if (ctx) ctx.scale(ratio, ratio);

    const pad = new SignaturePad(canvas, {
      backgroundColor: 'rgb(255, 255, 255)',
      penColor: 'rgb(0, 0, 0)',
      minWidth: 0.5,
      maxWidth: 2.5,
    });
    pad.addEventListener('endStroke', () => setHasSignature(!pad.isEmpty()));
    signaturePadRef.current = pad;
    return () => { pad.off(); };
  }, [step]);

  const clearSignature = useCallback(() => {
    signaturePadRef.current?.clear();
    setHasSignature(false);
  }, []);

  // ── Fahrzeug auswählen ──
  const selectFahrzeug = (fz: Fahrzeug) => {
    set('fahrzeug_id', fz.id);
    set('fahrzeug_kennzeichen', fz.kennzeichen);
    set('fahrzeug_marke', fz.marke);
    set('fahrzeug_modell', fz.modell);
    set('fahrzeug_kategorie', fz.kategorie);
    set('fahrzeug_km_stand', fz.km_aktuell || 0);
  };

  // ── Mietdauer berechnen ──
  const mietdauer = (() => {
    if (!form.mietbeginn || !form.mietende) return 0;
    const start = new Date(form.mietbeginn);
    const end = new Date(form.mietende);
    const diff = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    return Math.max(0, diff);
  })();

  const gesamtNetto = mietdauer * form.tagessatz_netto;
  const eigenersparnis = gesamtNetto * (form.eigenersparnis_prozent / 100);
  const nettoNachAbzug = gesamtNetto - eigenersparnis;
  const mwst = nettoNachAbzug * 0.19;
  const brutto = nettoNachAbzug + mwst;

  // ── Vertrag speichern ──
  const handleSubmit = async () => {
    if (!agbAccepted || !datenschutzAccepted || !hasSignature) {
      toast.error('Bitte akzeptieren Sie alle Bedingungen und unterschreiben Sie.');
      return;
    }

    setIsGeneratingPdf(true);
    try {
      const signatureDataUrl = signaturePadRef.current?.toDataURL('image/png') || '';
      const vertragNr = `LCS-${new Date().getFullYear()}-${String(Date.now()).slice(-6)}`;

      // 1. Forderungsakte erstellen
      const { data: akte, error: akteError } = await supabase
        .from('forderungsakten')
        .insert({
          akten_nr: `MG-${vertragNr}`,
          unfall_datum: form.unfall_datum || null,
          unfall_ort: form.unfall_ort || null,
          unfallgegner_name: form.unfallgegner_name || null,
          unfallgegner_kennzeichen: form.unfallgegner_kennzeichen || null,
          versicherung_gegner: form.versicherung_gegner || null,
          schaden_nummer: form.schaden_nummer || null,
          fahrzeugklasse_beschaedigt: form.beschaedigt_klasse || null,
          fahrzeugklasse_gemietet: form.fahrzeug_kategorie || null,
          klassengleichheit: (form.beschaedigt_klasse || '').toLowerCase() === (form.fahrzeug_kategorie || '').toLowerCase(),
          mietvertrag_nr: vertragNr,
          mietbeginn: form.mietbeginn || null,
          mietende: form.mietende || null,
          tagessatz: form.tagessatz_netto,
          eigenersparnis_prozent: form.eigenersparnis_prozent,
          gesamtbetrag: brutto,
          status: 'offen',
          mahnstufe: 'keine',
          anspruchsgrundlage: '§ 249 BGB - Naturalrestitution',
        })
        .select()
        .single();

      if (akteError) throw akteError;

      // 2. PDF generieren
      const { default: jsPDF } = await import('jspdf');
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pw = doc.internal.pageSize.getWidth();
      const m = 15;
      const cw = pw - 2 * m;
      let y = 15;

      // Briefkopf
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('LCS GmbH — Mietwagenservice', m, y); y += 5;
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text('Rahmedestrasse 65, 58507 Luedenscheid | Tel: 02351 / 87 69 860', m, y); y += 3;
      doc.text('info@gutachter-koop.de | www.gutachter-koop.de', m, y); y += 2;
      doc.setDrawColor(196, 30, 58);
      doc.setLineWidth(0.8);
      doc.line(m, y, pw - m, y); y += 8;

      // Titel
      doc.setFontSize(13);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 30, 30);
      doc.text('Mietvertrag Unfallersatzfahrzeug', m, y); y += 5;
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text(`Vertrag-Nr.: ${vertragNr}`, m, y); y += 4;
      doc.text(`Datum: ${unterschriftDatum}`, m, y); y += 8;

      // Mieter
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('1. Mieter', m, y); y += 5;
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text(`${form.mieter_anrede} ${form.mieter_vorname} ${form.mieter_nachname}`, m, y); y += 4;
      doc.text(`${form.mieter_strasse}, ${form.mieter_plz} ${form.mieter_ort}`, m, y); y += 4;
      doc.text(`Tel: ${form.mieter_telefon} | E-Mail: ${form.mieter_email}`, m, y); y += 4;
      doc.text(`Fuehrerschein: ${form.mieter_fuehrerschein_nr} (Klasse ${form.mieter_fuehrerschein_klasse})`, m, y); y += 8;

      // Unfall
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('2. Unfallangaben', m, y); y += 5;
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text(`Unfalldatum: ${form.unfall_datum} | Unfallort: ${form.unfall_ort}`, m, y); y += 4;
      doc.text(`Unfallgegner: ${form.unfallgegner_name} (${form.unfallgegner_kennzeichen})`, m, y); y += 4;
      doc.text(`Versicherung: ${form.versicherung_gegner} | Schaden-Nr.: ${form.schaden_nummer}`, m, y); y += 8;

      // Mietfahrzeug
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('3. Mietfahrzeug', m, y); y += 5;
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text(`${form.fahrzeug_marke} ${form.fahrzeug_modell} | ${form.fahrzeug_kennzeichen}`, m, y); y += 4;
      doc.text(`Kategorie: ${form.fahrzeug_kategorie} | KM-Stand bei Uebergabe: ${form.fahrzeug_km_stand.toLocaleString('de-DE')} km`, m, y); y += 8;

      // Konditionen
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('4. Mietkonditionen', m, y); y += 5;
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text(`Mietbeginn: ${form.mietbeginn} | Mietende: ${form.mietende || 'offen'}`, m, y); y += 4;
      doc.text(`Tagessatz netto: ${formatEuro(form.tagessatz_netto)} | Mietdauer: ${mietdauer} Tage`, m, y); y += 4;
      doc.text(`Gesamt netto: ${formatEuro(gesamtNetto)}`, m, y); y += 4;
      doc.text(`Eigenersparnis (${form.eigenersparnis_prozent}%): -${formatEuro(eigenersparnis)}`, m, y); y += 4;
      doc.text(`Netto nach Abzug: ${formatEuro(nettoNachAbzug)}`, m, y); y += 4;
      doc.text(`MwSt. (19%): ${formatEuro(mwst)}`, m, y); y += 4;
      doc.setFont('helvetica', 'bold');
      doc.text(`Gesamtbetrag brutto: ${formatEuro(brutto)}`, m, y); y += 4;
      doc.setFont('helvetica', 'normal');
      doc.text(`Kaution: ${formatEuro(form.kaution)} | Frei-km/Tag: ${form.km_frei_pro_tag} | Mehr-km: ${formatEuro(form.km_mehrpreis)}/km`, m, y); y += 8;

      // AGB
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('5. Allgemeine Mietbedingungen', m, y); y += 5;
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      const agbText = 'Der Mieter verpflichtet sich, das Fahrzeug pfleglich zu behandeln und nur im oeffentlichen Strassenverkehr zu nutzen. Das Fahrzeug darf nur vom Mieter oder einer im Vertrag genannten Person gefuehrt werden. Bei Unfaellen oder Schaeden ist der Vermieter unverzueglich zu informieren. Die Abrechnung der Mietwagenkosten erfolgt gegenueber der gegnerischen Haftpflichtversicherung gemaess § 249 BGB. Der Mieter tritt seine Ansprueche auf Erstattung der Mietwagenkosten an den Vermieter ab. Die Kaution wird bei ordnungsgemaesser Rueckgabe des Fahrzeugs erstattet.';
      const agbLines = doc.splitTextToSize(agbText, cw);
      doc.text(agbLines, m, y);
      y += agbLines.length * 3 + 8;

      // Unterschrift
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('6. Unterschrift', m, y); y += 5;
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text(`Ort, Datum: Luedenscheid, ${unterschriftDatum}`, m, y); y += 8;

      // Unterschrift-Bild einfügen
      if (signatureDataUrl) {
        doc.addImage(signatureDataUrl, 'PNG', m, y, 60, 20);
        y += 22;
      }
      doc.line(m, y, m + 70, y); y += 4;
      doc.text(`${form.mieter_vorname} ${form.mieter_nachname} (Mieter)`, m, y);

      // PDF als Blob
      const pdfBlob = doc.output('blob');
      const pdfFileName = `Mietvertrag_${vertragNr}_${form.mieter_nachname}.pdf`;

      // 3. PDF in Supabase Storage hochladen
      const { error: uploadError } = await supabase.storage
        .from('dokumente')
        .upload(`mietvertraege/${pdfFileName}`, pdfBlob, { contentType: 'application/pdf' });

      if (uploadError) {
        console.warn('Storage upload failed:', uploadError);
      }

      // 4. Dokument in forderungs_dokumente verknüpfen
      if (akte) {
        await supabase.from('forderungs_dokumente').insert({
          forderungsakte_id: akte.id,
          dokument_typ: 'mietvertrag',
          dateiname: pdfFileName,
          dateipfad: `mietvertraege/${pdfFileName}`,
          beschreibung: `Mietvertrag ${vertragNr} — ${form.mieter_vorname} ${form.mieter_nachname}`,
        });
      }

      // PDF auch zum Download anbieten
      doc.save(pdfFileName);

      toast.success(`Mietvertrag ${vertragNr} erstellt und gespeichert!`);
      
      // Reset
      setForm({ ...EMPTY_FORM });
      setStep(1);
      setShowList(true);
      setHasSignature(false);
      setAgbAccepted(false);
      setDatenschutzAccepted(false);

      // Verträge neu laden
      const { data: newVertraege } = await supabase
        .from('forderungsakten')
        .select('*')
        .not('mietvertrag_nr', 'is', null)
        .order('created_at', { ascending: false });
      setVertraege(newVertraege || []);

    } catch (err: any) {
      console.error('Mietvertrag error:', err);
      toast.error(`Fehler: ${err.message || 'Unbekannter Fehler'}`);
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  // ── Validierung pro Schritt ──
  const canProceed = (() => {
    switch (step) {
      case 1: return form.mieter_vorname && form.mieter_nachname;
      case 2: return true; // Optional
      case 3: return !!form.fahrzeug_id;
      case 4: return form.tagessatz_netto > 0 && form.mietbeginn;
      case 5: return agbAccepted && datenschutzAccepted && hasSignature;
      default: return true;
    }
  })();

  // ── RENDER ──

  if (showList) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Mietverträge</h1>
            <p className="text-sm text-gray-500 mt-1">Digitale Mietverträge für Unfallersatzfahrzeuge</p>
          </div>
          <button
            onClick={() => { setShowList(false); setStep(1); setForm({ ...EMPTY_FORM }); }}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-white font-semibold text-sm shadow-md hover:opacity-90 transition-all"
            style={{ background: `linear-gradient(135deg, ${KOOP_ROT}, #9B1627)` }}
          >
            <Plus size={16} /> Neuer Mietvertrag
          </button>
        </div>

        {/* Suche */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Suche nach Name, Vertragsnummer, Kennzeichen..."
            className="pl-9"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-[10px] font-semibold text-gray-400 uppercase">Verträge gesamt</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{vertraege.length}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-[10px] font-semibold text-gray-400 uppercase">Aktive Mieten</p>
            <p className="text-2xl font-bold text-emerald-600 mt-1">
              {vertraege.filter(v => v.status === 'offen').length}
            </p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-[10px] font-semibold text-gray-400 uppercase">Verfügbare Fahrzeuge</p>
            <p className="text-2xl font-bold text-blue-600 mt-1">{fahrzeuge.length}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-[10px] font-semibold text-gray-400 uppercase">Offenes Volumen</p>
            <p className="text-2xl font-bold mt-1" style={{ color: KOOP_ROT }}>
              {formatEuro(vertraege.reduce((sum, v) => sum + (v.gesamtbetrag || 0), 0))}
            </p>
          </div>
        </div>

        {/* Vertragsliste */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <h3 className="text-sm font-bold text-gray-700">Mietverträge</h3>
          </div>
          {vertraege.length === 0 ? (
            <div className="p-8 text-center">
              <Car className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500">Noch keine Mietverträge vorhanden</p>
              <p className="text-xs text-gray-400 mt-1">Erstellen Sie Ihren ersten digitalen Mietvertrag</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {vertraege
                .filter(v => {
                  if (!searchTerm) return true;
                  const q = searchTerm.toLowerCase();
                  return (v.unfallgegner_name || '').toLowerCase().includes(q) ||
                    (v.mietvertrag_nr || '').toLowerCase().includes(q) ||
                    (v.akten_nr || '').toLowerCase().includes(q);
                })
                .map(v => (
                  <div key={v.id} className="px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-all">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${KOOP_ROT}15` }}>
                        <FileText size={16} style={{ color: KOOP_ROT }} />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-800">{v.unfallgegner_name || 'Unbekannt'}</p>
                        <p className="text-[10px] text-gray-400">{v.mietvertrag_nr || v.akten_nr} | {v.mietbeginn || '—'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                        v.status === 'offen' ? 'bg-amber-100 text-amber-700' :
                        v.status === 'bezahlt' ? 'bg-emerald-100 text-emerald-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {v.status || 'offen'}
                      </span>
                      <span className="text-sm font-bold text-gray-800">
                        {v.gesamtbetrag ? formatEuro(Number(v.gesamtbetrag)) : '—'}
                      </span>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── FORMULAR (Multi-Step) ──
  return (
    <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => setShowList(true)} className="p-2 rounded-lg hover:bg-gray-100 transition-all">
          <ChevronLeft size={20} className="text-gray-500" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-gray-900">Neuer Mietvertrag</h1>
          <p className="text-xs text-gray-500">Unfallersatzfahrzeug — Digitaler Vertrag mit Unterschrift</p>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="flex items-center gap-1">
        {STEPS.map((s, i) => (
          <div key={s.id} className="flex-1 flex items-center gap-1">
            <button
              onClick={() => s.id <= step && setStep(s.id)}
              className={`flex-1 h-1.5 rounded-full transition-all ${
                s.id <= step ? 'bg-gradient-to-r from-red-500 to-red-600' : 'bg-gray-200'
              }`}
            />
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between px-1">
        {STEPS.map(s => (
          <div key={s.id} className={`text-center ${s.id === step ? 'opacity-100' : 'opacity-40'}`}>
            <s.icon size={14} className="mx-auto mb-0.5" style={{ color: s.id <= step ? KOOP_ROT : '#9CA3AF' }} />
            <p className="text-[9px] font-semibold text-gray-600">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Step Content */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 space-y-5">

        {/* STEP 1: Mieter */}
        {step === 1 && (
          <>
            <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
              <User size={16} style={{ color: KOOP_ROT }} /> Mieterdaten
            </h3>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-[10px]">Anrede</Label>
                <select value={form.mieter_anrede} onChange={e => set('mieter_anrede', e.target.value)}
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-200 text-sm">
                  <option>Herr</option><option>Frau</option><option>Divers</option>
                </select>
              </div>
              <div><Label className="text-[10px]">Vorname *</Label><Input className="mt-1" value={form.mieter_vorname} onChange={e => set('mieter_vorname', e.target.value)} /></div>
              <div><Label className="text-[10px]">Nachname *</Label><Input className="mt-1" value={form.mieter_nachname} onChange={e => set('mieter_nachname', e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2"><Label className="text-[10px]">Straße</Label><Input className="mt-1" value={form.mieter_strasse} onChange={e => set('mieter_strasse', e.target.value)} /></div>
              <div><Label className="text-[10px]">PLZ</Label><Input className="mt-1" value={form.mieter_plz} onChange={e => set('mieter_plz', e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-[10px]">Ort</Label><Input className="mt-1" value={form.mieter_ort} onChange={e => set('mieter_ort', e.target.value)} /></div>
              <div><Label className="text-[10px]">Telefon</Label><Input className="mt-1" value={form.mieter_telefon} onChange={e => set('mieter_telefon', e.target.value)} /></div>
              <div><Label className="text-[10px]">E-Mail</Label><Input className="mt-1" type="email" value={form.mieter_email} onChange={e => set('mieter_email', e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-[10px]">Führerschein-Nr.</Label><Input className="mt-1" value={form.mieter_fuehrerschein_nr} onChange={e => set('mieter_fuehrerschein_nr', e.target.value)} /></div>
              <div><Label className="text-[10px]">Klasse</Label><Input className="mt-1" value={form.mieter_fuehrerschein_klasse} onChange={e => set('mieter_fuehrerschein_klasse', e.target.value)} /></div>
              <div><Label className="text-[10px]">Geburtsdatum</Label><Input className="mt-1" type="date" value={form.mieter_geburtsdatum} onChange={e => set('mieter_geburtsdatum', e.target.value)} /></div>
            </div>
          </>
        )}

        {/* STEP 2: Unfall */}
        {step === 2 && (
          <>
            <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
              <AlertCircle size={16} style={{ color: KOOP_ROT }} /> Unfalldaten & Versicherung
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-[10px]">Unfalldatum</Label><Input className="mt-1" type="date" value={form.unfall_datum} onChange={e => set('unfall_datum', e.target.value)} /></div>
              <div><Label className="text-[10px]">Unfallort</Label><Input className="mt-1" value={form.unfall_ort} onChange={e => set('unfall_ort', e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-[10px]">Unfallgegner Name</Label><Input className="mt-1" value={form.unfallgegner_name} onChange={e => set('unfallgegner_name', e.target.value)} /></div>
              <div><Label className="text-[10px]">Unfallgegner Kennzeichen</Label><Input className="mt-1" value={form.unfallgegner_kennzeichen} onChange={e => set('unfallgegner_kennzeichen', e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-[10px]">Versicherung (Gegner)</Label><Input className="mt-1" value={form.versicherung_gegner} onChange={e => set('versicherung_gegner', e.target.value)} /></div>
              <div><Label className="text-[10px]">Schaden-Nr.</Label><Input className="mt-1" value={form.schaden_nummer} onChange={e => set('schaden_nummer', e.target.value)} /></div>
            </div>
            <div className="border-t border-gray-100 pt-4 mt-2">
              <h4 className="text-xs font-bold text-gray-600 mb-3">Beschädigtes Fahrzeug des Mieters</h4>
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="text-[10px]">Kennzeichen</Label><Input className="mt-1" value={form.beschaedigt_kennzeichen} onChange={e => set('beschaedigt_kennzeichen', e.target.value)} /></div>
                <div><Label className="text-[10px]">Marke</Label><Input className="mt-1" value={form.beschaedigt_marke} onChange={e => set('beschaedigt_marke', e.target.value)} /></div>
                <div><Label className="text-[10px]">Modell</Label><Input className="mt-1" value={form.beschaedigt_modell} onChange={e => set('beschaedigt_modell', e.target.value)} /></div>
              </div>
              <div className="mt-3">
                <Label className="text-[10px]">Fahrzeugklasse (beschädigt)</Label>
                <select value={form.beschaedigt_klasse} onChange={e => set('beschaedigt_klasse', e.target.value)}
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-200 text-sm">
                  <option value="">— Bitte wählen —</option>
                  <option value="kleinwagen">Kleinwagen</option>
                  <option value="limousine">Limousine / Kompaktklasse</option>
                  <option value="kombi">Kombi</option>
                  <option value="suv">SUV / Geländewagen</option>
                  <option value="van">Van / Transporter</option>
                </select>
              </div>
            </div>
          </>
        )}

        {/* STEP 3: Fahrzeug */}
        {step === 3 && (
          <>
            <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
              <Car size={16} style={{ color: KOOP_ROT }} /> Mietfahrzeug wählen
            </h3>
            {form.beschaedigt_klasse && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-2.5">
                <p className="text-[11px] text-blue-700">
                  <strong>Hinweis:</strong> Das beschädigte Fahrzeug ist Klasse <strong>{form.beschaedigt_klasse}</strong>.
                  Für Klassengleichheit sollte ein gleichwertiges Fahrzeug gewählt werden.
                </p>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {fahrzeuge.map(fz => (
                <button
                  key={fz.id}
                  onClick={() => selectFahrzeug(fz)}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    form.fahrzeug_id === fz.id
                      ? 'border-red-400 bg-red-50 shadow-sm'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-bold text-gray-800">{fz.marke} {fz.modell}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{fz.kennzeichen}</p>
                    </div>
                    {form.fahrzeug_id === fz.id && (
                      <CheckCircle2 size={20} className="text-red-500" />
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-2">
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 font-medium">{fz.kategorie}</span>
                    <span className="text-[10px] text-gray-400">{(fz.km_aktuell || 0).toLocaleString('de-DE')} km</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                      fz.kategorie === form.beschaedigt_klasse ? 'bg-emerald-100 text-emerald-700' : ''
                    }`}>
                      {fz.kategorie === form.beschaedigt_klasse ? 'Klassengleich' : ''}
                    </span>
                  </div>
                </button>
              ))}
            </div>
            {fahrzeuge.length === 0 && (
              <div className="text-center py-8">
                <Car className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">Keine verfügbaren Fahrzeuge</p>
              </div>
            )}
          </>
        )}

        {/* STEP 4: Konditionen */}
        {step === 4 && (
          <>
            <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
              <Euro size={16} style={{ color: KOOP_ROT }} /> Mietkonditionen
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-[10px]">Mietbeginn *</Label><Input className="mt-1" type="date" value={form.mietbeginn} onChange={e => set('mietbeginn', e.target.value)} /></div>
              <div><Label className="text-[10px]">Mietende (voraussichtlich)</Label><Input className="mt-1" type="date" value={form.mietende} onChange={e => set('mietende', e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-[10px]">Tagessatz netto (EUR) *</Label><Input className="mt-1" type="number" value={form.tagessatz_netto} onChange={e => set('tagessatz_netto', Number(e.target.value))} /></div>
              <div><Label className="text-[10px]">Eigenersparnis (%)</Label><Input className="mt-1" type="number" step="0.5" value={form.eigenersparnis_prozent} onChange={e => set('eigenersparnis_prozent', Number(e.target.value))} /></div>
              <div><Label className="text-[10px]">Kaution (EUR)</Label><Input className="mt-1" type="number" value={form.kaution} onChange={e => set('kaution', Number(e.target.value))} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-[10px]">Frei-km pro Tag</Label><Input className="mt-1" type="number" value={form.km_frei_pro_tag} onChange={e => set('km_frei_pro_tag', Number(e.target.value))} /></div>
              <div><Label className="text-[10px]">Mehr-km Preis (EUR)</Label><Input className="mt-1" type="number" step="0.01" value={form.km_mehrpreis} onChange={e => set('km_mehrpreis', Number(e.target.value))} /></div>
            </div>
            <div><Label className="text-[10px]">Notizen</Label><textarea className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" rows={3} value={form.notizen} onChange={e => set('notizen', e.target.value)} /></div>

            {/* Kalkulation */}
            {mietdauer > 0 && (
              <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 space-y-2">
                <h4 className="text-xs font-bold text-gray-600">Kalkulation</h4>
                <div className="flex justify-between text-xs text-gray-600"><span>{mietdauer} Tage x {formatEuro(form.tagessatz_netto)}</span><span>{formatEuro(gesamtNetto)}</span></div>
                <div className="flex justify-between text-xs text-gray-500"><span>Eigenersparnis ({form.eigenersparnis_prozent}%)</span><span>-{formatEuro(eigenersparnis)}</span></div>
                <div className="flex justify-between text-xs text-gray-600 border-t border-gray-200 pt-2"><span>Netto nach Abzug</span><span>{formatEuro(nettoNachAbzug)}</span></div>
                <div className="flex justify-between text-xs text-gray-500"><span>MwSt. (19%)</span><span>{formatEuro(mwst)}</span></div>
                <div className="flex justify-between text-sm font-bold pt-2 border-t border-gray-300" style={{ color: KOOP_ROT }}><span>Gesamtbetrag brutto</span><span>{formatEuro(brutto)}</span></div>
              </div>
            )}
          </>
        )}

        {/* STEP 5: Vertrag & Unterschrift */}
        {step === 5 && (
          <>
            <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
              <FileText size={16} style={{ color: KOOP_ROT }} /> Vertrag prüfen & unterschreiben
            </h3>

            {/* Zusammenfassung */}
            <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 space-y-3 text-xs">
              <div className="grid grid-cols-2 gap-2">
                <div><span className="text-gray-400">Mieter:</span> <span className="font-semibold text-gray-800">{form.mieter_anrede} {form.mieter_vorname} {form.mieter_nachname}</span></div>
                <div><span className="text-gray-400">Adresse:</span> <span className="text-gray-700">{form.mieter_strasse}, {form.mieter_plz} {form.mieter_ort}</span></div>
                <div><span className="text-gray-400">Fahrzeug:</span> <span className="font-semibold text-gray-800">{form.fahrzeug_marke} {form.fahrzeug_modell} ({form.fahrzeug_kennzeichen})</span></div>
                <div><span className="text-gray-400">Mietdauer:</span> <span className="text-gray-700">{form.mietbeginn} bis {form.mietende || 'offen'} ({mietdauer} Tage)</span></div>
                <div><span className="text-gray-400">Versicherung:</span> <span className="text-gray-700">{form.versicherung_gegner || '—'}</span></div>
                <div><span className="text-gray-400">Schaden-Nr.:</span> <span className="text-gray-700">{form.schaden_nummer || '—'}</span></div>
              </div>
              <div className="border-t border-gray-200 pt-2 flex justify-between font-bold" style={{ color: KOOP_ROT }}>
                <span>Gesamtbetrag brutto:</span><span>{formatEuro(brutto)}</span>
              </div>
            </div>

            {/* AGB */}
            <div className="space-y-2.5">
              <CheckboxRow checked={agbAccepted} onChange={setAgbAccepted}
                label="Ich akzeptiere die Allgemeinen Mietbedingungen der LCS GmbH. Das Fahrzeug wird pfleglich behandelt und nur im öffentlichen Straßenverkehr genutzt." />
              <CheckboxRow checked={datenschutzAccepted} onChange={setDatenschutzAccepted}
                label="Ich bin damit einverstanden, dass meine Daten zur Abwicklung des Mietvertrags und zur Geltendmachung der Mietwagenkosten gegenüber der gegnerischen Versicherung verarbeitet werden." />
            </div>

            {/* Unterschrift */}
            <div className="bg-white rounded-2xl border-2 border-gray-200 shadow-sm overflow-hidden">
              <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 border-b border-gray-200">
                <div className="flex items-center gap-2">
                  <Pen size={14} className="text-gray-500" />
                  <span className="text-[12px] font-bold text-gray-700">Unterschrift Mieter</span>
                </div>
                <div className="flex items-center gap-2">
                  <input type="text" value={unterschriftDatum} onChange={e => setUnterschriftDatum(e.target.value)}
                    className="text-[11px] text-gray-500 bg-transparent border-b border-dashed border-gray-300 focus:outline-none focus:border-gray-500 w-36 text-center" />
                  {hasSignature && (
                    <button onClick={clearSignature}
                      className="flex items-center gap-1 px-2 py-1 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 text-[10px] font-semibold transition-all">
                      <Trash2 size={12} /> Löschen
                    </button>
                  )}
                </div>
              </div>
              <div className="relative">
                <canvas ref={canvasRef} className="w-full touch-none" style={{ height: '140px', cursor: 'crosshair' }} />
                {!hasSignature && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <p className="text-[13px] text-gray-300 font-medium">Hier unterschreiben</p>
                  </div>
                )}
              </div>
              <div className="px-4 py-2 bg-gray-50 border-t border-gray-200">
                <p className="text-[10px] text-gray-400">Unterschrift {form.mieter_vorname} {form.mieter_nachname} (Mieter)</p>
              </div>
            </div>

            {/* DSGVO Hinweis */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 flex items-start gap-3">
              <Shield size={14} className="text-blue-500 mt-0.5 flex-shrink-0" />
              <p className="text-[11px] text-blue-700 leading-relaxed">
                <strong>Hinweis:</strong> Die Abrechnung der Mietwagenkosten erfolgt gegenüber der gegnerischen Haftpflichtversicherung gemäß § 249 BGB. Für Sie als Geschädigter entstehen keine Kosten.
              </p>
            </div>
          </>
        )}
      </div>

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => step > 1 ? setStep(step - 1) : setShowList(true)}
          className="flex items-center gap-2 px-5 py-3 rounded-xl border-2 border-gray-200 hover:bg-gray-50 transition-all text-sm font-semibold text-gray-600"
        >
          <ChevronLeft size={16} /> {step === 1 ? 'Abbrechen' : 'Zurück'}
        </button>

        {step < 5 ? (
          <button
            onClick={() => setStep(step + 1)}
            disabled={!canProceed}
            className="flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold text-white shadow-md hover:opacity-90 transition-all disabled:opacity-40"
            style={{ background: `linear-gradient(135deg, ${KOOP_ROT}, #9B1627)` }}
          >
            Weiter <ChevronRight size={16} />
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            disabled={!canProceed || isGeneratingPdf}
            className="flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold text-white shadow-md hover:opacity-90 transition-all disabled:opacity-40"
            style={{ background: `linear-gradient(135deg, ${KOOP_ROT}, #9B1627)` }}
          >
            {isGeneratingPdf ? (
              <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Wird erstellt...</>
            ) : (
              <><CheckCircle2 size={16} /> Vertrag unterschreiben</>
            )}
          </button>
        )}
      </div>
    </div>
  );
}

// ── Checkbox Row ──
function CheckboxRow({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <button onClick={() => onChange(!checked)}
      className={`w-full flex items-start gap-3 p-3 rounded-xl border-2 text-left transition-all ${
        checked ? 'border-emerald-400 bg-emerald-50' : 'border-gray-200 bg-white hover:border-gray-300'
      }`}>
      <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${
        checked ? 'border-emerald-500 bg-emerald-500' : 'border-gray-300'
      }`}>
        {checked && <CheckCircle2 size={12} className="text-white" />}
      </div>
      <span className="text-[11px] text-gray-700 leading-relaxed">{label}</span>
    </button>
  );
}
